import { LoginResponse } from '@/auth/interface/auth.interface';
import { LicenseState } from '@/enums/licenseState';
import { CompleteLicense } from '@/interfaces/dashboard/licenses';
import { DocumentService } from '@/services/document/document.service';
import { RequirementService } from '@/services/requirement/requirement.service';
import { StoreState } from '@/store';
import { State } from '@/store/login/reducer';
import { getCurrentUserData } from '@/store/login/selectors';
import { modalShow } from '@/store/modal/actions';
import { normalize } from '@/utils/normalizeString';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { BreakpointObserver } from '@angular/cdk/layout';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { FormControl } from '@angular/forms';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { Store } from '@ngrx/store';
import { Observable, Observer, of, combineLatest } from 'rxjs';
import * as JSZip from 'jszip';

@Component({
  selector: 'app-license-card',
  templateUrl: './license-card.component.html',
  styleUrls: ['./license-card.component.css', './license-card.component2.css'],
  encapsulation: ViewEncapsulation.None,
  animations: [
    trigger('openLicense', [
      state('open', style({
        overflow: 'hidden',
      })),
      state('close', style({
        padding: '0px',
        margin: '0px',
        overflow: 'hidden',
        height: '0px',
      })),
      transition('open => close', [
        animate(280, style({ padding: '0px', margin: '0px', height: '0px', transition: 'ease' })),
      ]),
      transition('close => open', [
        animate(280, style({})),
      ]),
      state('openArrow', style({
        transform: 'rotate(180deg)',
      })),
      state('closeArrow', style({
        transform: 'rotate(0)',
      })),
      transition('openArrow => closeArrow', [
        animate(200, style({ transform: 'rotate(0)', transition: 'ease-in' })),
      ]),
      transition('closeArrow => openArrow', [
        animate(200, style({ transform: 'rotate(180deg)', transition: 'ease-out' })),
      ]),
    ]),
    trigger('openVehicle', [
      state('openArrow', style({
        transform: 'rotate(180deg)',
      })),
      state('closeArrow', style({
        transform: 'rotate(0)',
      })),
      transition('openArrow => closeArrow', [
        animate(200, style({ transform: 'rotate(0)', transition: 'ease-in' })),
      ]),
      transition('closeArrow => openArrow', [
        animate(200, style({ transform: 'rotate(180deg)', transition: 'ease-out' })),
      ]),
    ]),
  ],
})
export class LicenseCardComponent implements OnInit {

  _userData?: State;

  @Input() userData?: State;
  @Input() usingAspect: 'DASHBOARD' | 'MY_PROFILE' | 'INITIAL' | 'EXTEND_LICENSE' = 'INITIAL';
  @Input() licensesData: Partial<CompleteLicense> = {};
  @Input() licenseIndex: number = 0;
  @Input() zonesRows: Array<number[]> = [];
  @Input() licensesLength: number = 0;
  @Input() selectedOption: 'EQUIPMENT' | 'LICENSE_TYPE' | 'ZONE' | 'NONE' = 'NONE';

  @Input() controlForm = new FormControl(null);

  showVehicles: Array<boolean> = [];
  sanitizedBase64Image: SafeUrl = '';
  qrImage: SafeUrl = '';

  show0 = false;
  isMobile: boolean = false;

  downloadingList: string[] = [];

  constructor(
    private store: Store<StoreState>,
    private domSanitizer: DomSanitizer,
    private breakpointObserver: BreakpointObserver,
    private documentService: DocumentService,
    private requirementService: RequirementService,
  ) {
    this.breakpointObserver.observe(['(max-width:767px)'])
      .subscribe((result) => {
        if (result.matches) {
          this.isMobile = result.matches;
          this.show0 = true;
        }
        else {
          this.isMobile = result.matches;
          this.show0 = false;
        }
      });
  }

  ngOnInit(): void {
    const userObserver: Observer<LoginResponse> = {
      next: (userData) => {
        this._userData = userData;
        let base64Image = this._userData.photo;
        base64Image = base64Image?.replace("'", '');
        if (base64Image) {
          this.sanitizedBase64Image = this.domSanitizer.bypassSecurityTrustUrl(base64Image);
        }
      },
      error: () => { },
      complete: () => { },
    };

    if (this.userData) {
      of(this.userData).subscribe(userObserver);
    }
    else {
      getCurrentUserData(this.store).subscribe(userObserver);
    }

    if (this.licensesData.id) {
      if (!this.qrImage) {
        this.requirementService.getLicenseQr(this.licensesData.id).subscribe({
          next: async (imageBlob) => {
            if (imageBlob) {
              const objectURL = URL.createObjectURL(imageBlob);
              this.qrImage = this.domSanitizer.bypassSecurityTrustUrl(objectURL);
            }
          },
        });
      }
    }

    this.toRenewLicense();
  }

  get menuState0() {
    return this.show0 ? 'open' : 'close';
  }

  get arrowState0() {
    return this.show0 ? 'openArrow' : 'closeArrow';
  }

  toggle() {
    this.show0 = !this.show0;
  }

  toggleVehicle(id: number) {
    this.showVehicles[id] = this.showVehicles[id] || false;
    this.showVehicles[id] = !this.showVehicles[id];
  }

  toRenewLicense(): boolean {
    let toRenew: boolean = false;
    if (this.usingAspect === 'DASHBOARD') {
      toRenew = this.licensesData.equipments?.some((vehicle) => vehicle.equipmentStatus === LicenseState.VENCIDA) || false;
    }
    if (toRenew || this.licensesLength === 1) {
      this.show0 = true;
    }
    return toRenew;
  }

  parseErrorBlob(err: HttpErrorResponse, type: 'JSON' | 'text'): Observable<any> {
    const reader: FileReader = new FileReader();

    const obs = Observable.create((observer: any) => {
      reader.onloadend = () => {
        if (type === 'text') {
          observer.next(reader.result);
        }
        else if (type === 'JSON') {
          if (typeof reader.result === 'string') {
            observer.next(JSON.parse(reader.result));
          }
        }
        observer.complete();
      };
    });
    reader.readAsText(err.error);
    return obs;
  }

  downloadPocketLicence(id: number) {
    const downloadId = `${id}-P`;

    if (this.isCurrentlyDownloading(downloadId)) return;

    this.addToDownloadingList(downloadId);

    this.documentService.downloadDocumentFilePocketLicence(id).subscribe({
      next: (blob) => {
        const a = document.createElement('a');
        const objectUrl = URL.createObjectURL(blob);
        a.href = objectUrl;
        a.download = `pocket-licence_${id}`;
        a.click();
        URL.revokeObjectURL(objectUrl);

        this.removeFromDownloadingList(downloadId);
      },
      error: (err) => {
        this.removeFromDownloadingList(downloadId);
        this.errorDownloadLicenseHandler(err);
      },
    });
  }

  downloadLicence(id: number) {
    const downloadId = `${id}-C`;

    if (this.isCurrentlyDownloading(downloadId)) return;

    this.addToDownloadingList(downloadId);

    this.documentService.downloadDocumentFileLicence(id).subscribe({
      next: (blob) => {
        const a = document.createElement('a');
        const objectUrl = URL.createObjectURL(blob);
        a.href = objectUrl;
        a.download = `licence_${id}`;
        a.click();
        URL.revokeObjectURL(objectUrl);
        this.removeFromDownloadingList(downloadId);
      },
      error: (err) => {
        this.removeFromDownloadingList(downloadId);
        this.errorDownloadLicenseHandler(err);
      },
    });
  }

  downloadPdfsInAZip(id: number, licenseNumber: string) {
    const downloadId = `${id}-Z`;

    if (this.isCurrentlyDownloading(downloadId)) return;

    this.addToDownloadingList(downloadId);

    combineLatest(this.documentService.downloadDocumentFileLicence(id), this.documentService.downloadDocumentFilePocketLicence(id)).subscribe({
      next: ([complete, pocket]) => {
        const zipFile = new JSZip();

        zipFile.file('complete.pdf', complete)
          .file('pocket.pdf', pocket);

        zipFile.generateAsync({ type: 'blob' })
          .then((content) => {
            // see FileSaver.js
            const a = document.createElement('a');
            const objectUrl = URL.createObjectURL(content);

            a.href = objectUrl;
            a.download = `licence_${licenseNumber}`;
            a.click();
            URL.revokeObjectURL(objectUrl);

            this.removeFromDownloadingList(downloadId);
          });
      },
      error: (err) => {
        this.removeFromDownloadingList(downloadId);
        this.errorDownloadLicenseHandler(err);
      },
    });
  }

  errorDownloadLicenseHandler(error: HttpErrorResponse) {
    interface BackError {
      httpCode: number;
      httpMessage: string;
      httpUserFriendlyError: string;
    }

    this.parseErrorBlob(error, 'JSON').subscribe({
      next: (err: BackError) => {
        if (cleanError(err.httpMessage) == cleanError('Licencia eliminada, no es posible generar documento')) {
          this.store.dispatch(modalShow({
            title: 'La licencia esta eliminada.',
            text: 'No fue posible descargar el pdf ya que la licencia acaba de ser eliminada.',
            text2: 'Haga click en el siguiente boton para recargar la pagina.',
            actionOnAccept: () => {
              // eslint-disable-next-line no-restricted-globals
              location.reload();
            },
            acceptButtonText: 'Recargar la pagina',
            noShowButtonBack: true,
          }));
        }
      },
    });
  }

  changeColorByClass(vehicleState: string) {
    let classList = '';
    switch (vehicleState) {
      case LicenseState.VIGENTE:
        classList = 'tr tr--green';
        break;
      case LicenseState.PROXIMO_A_VENCER:
        classList = 'tr tr--yellow';
        break;
      case LicenseState.VENCIDA:
        classList = 'tr tr--red';
        break;
      default:
        classList = 'tr tr--red';
    }
    return classList;
  }

  counter(num: number) {
    return new Array(num);
  }

  isSuspended(sanctionType?: string) {
    return normalize(sanctionType) === normalize('Suspendida');
  }

  get selectedVehicleIndex(): number {
    const selectedVehicle = this.controlForm.value as CompleteLicense['equipments'][0];
    let selectedVehicleIndex = -1;
    if (selectedVehicle !== null) {
      this.licensesData.equipments?.every((vehicle, index) => {
        if (selectedVehicle.equipmentId === vehicle.equipmentId) {
          selectedVehicleIndex = index;
          return false;
        }
        return true;
      });
    }
    return selectedVehicleIndex;
  }
  onSelectEquipmentToEdit(vehicle: any) {
    this.controlForm.setValue(vehicle);
  }

  addToDownloadingList(id: string) {
    this.downloadingList.push(id);
  }

  removeFromDownloadingList(id: string) {
    const index = this.downloadingList.indexOf(id);
    if (index >= 0) {
      this.downloadingList.splice(index, 1);
    }
  }

  isCurrentlyDownloading(id: string): boolean {
    return this.downloadingList.some((x) => x === id);
  }

}

const cleanError = (text: string) => replaceChar(removeChar(normalize(text), '.'), '_', ' ').replace(/\s/g, '');
const removeChar = (text: string, characters: string) => text.split(characters).join('');
const replaceChar = (text: string, lookup: string, newChar: string) => text.split(lookup).join(newChar);


